import type { SecondaryDimensionValues } from '@revelio/core';
import {
  type CompositionV3Ent,
  type CompositionV4BreakdownReq,
  CompositionV4Dimension,
  EntKind,
} from '@revelio/data-access';

import type { LevelDimension } from '../DataProvider/types';
import { isLevelDimension, taxonomyOfLevel } from './taxonomy-levels';

const BREAKDOWN_DIMENSIONS = {
  gender: CompositionV4Dimension.Gender,
  ethnicity: CompositionV4Dimension.Ethnicity,
  education: CompositionV4Dimension.Education,
  seniority: CompositionV4Dimension.Seniority,
  geography: CompositionV4Dimension.Geography,
  rics: CompositionV4Dimension.Rics,
  role: CompositionV4Dimension.Role,
  skills: CompositionV4Dimension.Skill,
  activities: CompositionV4Dimension.Activity,
} as const satisfies Partial<
  Record<SecondaryDimensionValues, CompositionV4Dimension>
>;

export type CompositionV4BreakdownDimension = keyof typeof BREAKDOWN_DIMENSIONS;

/** What a table is broken down by: a dimension, or a taxonomy at a level. */
export type CompositionV4BreakdownColumn =
  | CompositionV4BreakdownDimension
  | LevelDimension;

export const isCompositionV4BreakdownDimension = (
  dimension: SecondaryDimensionValues
): dimension is CompositionV4BreakdownDimension =>
  dimension in BREAKDOWN_DIMENSIONS;

const LEVELED_DIMENSIONS: readonly CompositionV4BreakdownDimension[] = [
  'geography',
  'rics',
  'role',
  'skills',
  'activities',
];

export const isCompositionV4LeveledDimension = (
  dimension: CompositionV4BreakdownDimension
): boolean => LEVELED_DIMENSIONS.includes(dimension);

const TREE_DIMENSIONS = ['role', 'geography', 'skills', 'activities'] as const;

type CompositionV4BreakdownTreeDimension = (typeof TREE_DIMENSIONS)[number];

export const isCompositionV4BreakdownTreeDimension = (
  dimension: CompositionV4BreakdownDimension
): dimension is CompositionV4BreakdownTreeDimension =>
  TREE_DIMENSIONS.includes(dimension as CompositionV4BreakdownTreeDimension);

const DEFAULT_TOP_N = 7;
const TREE_LEVEL_TOP_N = 10;
const SUBLEVEL_TOP_N = 50;
const EVERY_CATEGORY_TOP_N = 2_147_483_647;

export const breakdownTopN = ({ top_n }: CompositionV4BreakdownReq): number =>
  top_n ?? DEFAULT_TOP_N;
const ENT_KIND_OF_LEVEL: ReadonlyMap<string, EntKind> = new Map(
  Object.values(EntKind).map((kind) => [kind.toLowerCase(), kind])
);

const breakdownOf = (
  column: CompositionV4BreakdownColumn
): CompositionV4BreakdownReq => {
  if (!isLevelDimension(column)) {
    return { dimension: BREAKDOWN_DIMENSIONS[column] };
  }
  const level = ENT_KIND_OF_LEVEL.get(column);
  return {
    dimension: BREAKDOWN_DIMENSIONS[taxonomyOfLevel(column)],
    ...(level ? { level } : {}),
  };
};

export const getCompositionV4TableBreakdowns = ({
  dimensions,
  timeseries,
  topN,
}: {
  dimensions: readonly CompositionV4BreakdownColumn[];
  timeseries: boolean;
  topN?: number;
}): CompositionV4BreakdownReq[] =>
  dimensions.map((column, index) => {
    const breakdown = breakdownOf(column);
    if (timeseries) return { ...breakdown, top_n: EVERY_CATEGORY_TOP_N };
    if (index > 0) return { ...breakdown, top_n: SUBLEVEL_TOP_N };
    if (dimensions.length > 1) return { ...breakdown, top_n: TREE_LEVEL_TOP_N };
    return topN === undefined ? breakdown : { ...breakdown, top_n: topN };
  });

export const getEntityBreakdowns = (
  dimension: CompositionV4BreakdownDimension,
  entities: readonly CompositionV3Ent[]
): CompositionV4BreakdownReq[] => {
  const idsByLevel = new Map<EntKind, number[]>();
  for (const { EntType, EntID } of entities) {
    idsByLevel.set(EntType, [...(idsByLevel.get(EntType) ?? []), EntID]);
  }
  return Array.from(idsByLevel, ([level, ids]) => ({
    dimension: BREAKDOWN_DIMENSIONS[dimension],
    level,
    ids,
  }));
};

const breakdownName = ({ dimension, level }: CompositionV4BreakdownReq) =>
  level ? `${dimension} ${level}` : dimension;

export const isRequestedBreakdown = (
  requested: CompositionV4BreakdownReq,
  { dimension, level }: { dimension: CompositionV4Dimension; level?: unknown }
): boolean =>
  requested.dimension === dimension &&
  (requested.level ?? null) === (level ?? null);

export const uniqueBreakdowns = (
  breakdowns: readonly CompositionV4BreakdownReq[]
): CompositionV4BreakdownReq[] => {
  const byName = new Map<string, CompositionV4BreakdownReq>();
  for (const breakdown of breakdowns) {
    const name = breakdownName(breakdown);
    const requested = byName.get(name);
    if (!requested || breakdownTopN(breakdown) > breakdownTopN(requested)) {
      byName.set(name, breakdown);
    }
  }
  return Array.from(byName.values());
};
